<template>
  <div class="flex h-screen overflow-hidden bg-slate-50 dark:bg-[#0B1120] text-slate-900 dark:text-[#F1F5F9] antialiased">
    <PWAbanner />
    <!-- Sidebar -->
    <aside
      v-if="showLayout"
      class="z-30 flex-shrink-0 h-full transition-all duration-300"
    >
      <SchoolSidebar />
    </aside>

    <!-- Main Content -->
    <div class="flex-1 flex flex-col overflow-hidden min-w-0 relative">
      <!-- Top Bar (Always Visible) -->
      <div
        v-if="showLayout"
        class="flex-shrink-0 sticky top-0 z-40"
      >
        <SchoolHeader />
      </div>

      <!-- Main Content Area with Ambient Depth -->
      <main class="flex-1 overflow-y-auto overflow-x-hidden px-3 sm:px-6 py-4 sm:py-6 bg-slate-50/70 dark:bg-[#0B1120] transition-colors relative">
        <!-- Subtle Ambient Radial Glow (Dark Mode Only) -->
        <div class="hidden dark:block pointer-events-none fixed top-0 right-0 w-[500px] h-[500px] bg-indigo-600/5 rounded-full blur-3xl -z-10"></div>
        <div class="hidden dark:block pointer-events-none fixed bottom-0 left-1/3 w-[600px] h-[600px] bg-violet-600/5 rounded-full blur-3xl -z-10"></div>

        <router-view />
      </main>
    </div>

    <!-- Global Toast -->
    <GlobalToast
      :message="toastState"
      @hide="hideToast"
    />
  </div>
</template>

<script setup>
  import { computed } from "vue";
  import { useRoute } from "vue-router";
  import { useStore } from "vuex";
  import PWAbanner from "@/components/PWAInstallBanner/PWAInstallBanner.vue";
  import SchoolHeader from "./components/Header/SchoolHeader.vue";
  import SchoolSidebar from "./components/Sidebar/SchoolSidebar.vue";
  import GlobalToast from "@/utils/GlobalToast.vue";
  import { useGlobalToast } from "@/utils/useGlobalToast";

  const store = useStore();
  const route = useRoute();

  // Auth route check
  const isAuthRoute = computed(() => {
    const path = route.path;
    return (
      path === "/login" ||
      path === "/signup" ||
      path === "/scanedTeacherAttendence" ||
      path === "/createSchool" ||
      path === "/404" ||
      path === "/forgot-password" ||
      path === "/onboarding" ||
      path === "/AttendanceMethods" ||
      /^\/mark-attendance\/[^/]+$/.test(path)
    );
  });

  // Total classes computation
  const TotalClasses = computed(() => {
    const classes = store.getters.allClasses || [];
    return classes.filter((c) => (c?.name || "").toLowerCase() !== "passout")
      .length;
  });

  // Determine if header/sidebar should be shown
  const showLayout = computed(() => {
    // Hide layout for auth routes
    if (isAuthRoute.value) return false;

    // Hide layout if no classes
    if (route.path === "/classes" && TotalClasses.value === 0) return false;

    return true;
  });

  // Toast state
  const { toastState, hideToast } = useGlobalToast();
</script>

<style>
  html,
  body,
  #app {
    height: 100%;
    margin: 0;
    padding: 0;
    font-size: 14.5px;
    background-color: #f9fafb;
    font-family: "Inter", Avenir, Helvetica, Arial, sans-serif;
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
  }

  html.dark,
  html.dark body,
  html.dark #app {
    background-color: #0B1120;
    color: #F1F5F9;
    -webkit-text-fill-color: initial;
  }

  /* Smooth scrolling for both */
  main,
  aside {
    scroll-behavior: smooth;
  }

  /* Scrollbar width */
  main::-webkit-scrollbar,
  aside::-webkit-scrollbar {
    width: 8px;
  }

  /* Scrollbar thumb */
  main::-webkit-scrollbar-thumb,
  aside::-webkit-scrollbar-thumb {
    background: #cbd5e1; /* Tailwind gray-300 */
    border-radius: 10px;
    transition: background 0.3s ease;
  }

  /* Hover effect */
  main::-webkit-scrollbar-thumb:hover,
  aside::-webkit-scrollbar-thumb:hover {
    background: #94a3b8; /* Tailwind gray-400 */
  }

  /* Optional scrollbar track background */
  main::-webkit-scrollbar-track,
  aside::-webkit-scrollbar-track {
    background: #f1f5f9; /* Tailwind gray-100 */
  }

  /* Dark mode scrollbars */
  html.dark main::-webkit-scrollbar-thumb,
  html.dark aside::-webkit-scrollbar-thumb {
    background: #263449;
  }

  html.dark main::-webkit-scrollbar-thumb:hover,
  html.dark aside::-webkit-scrollbar-thumb:hover {
    background: #3B82F6;
  }

  html.dark main::-webkit-scrollbar-track,
  html.dark aside::-webkit-scrollbar-track {
    background: #0B1120;
  }

  /* ==========================
     ✅ Fine-Tuned Breakpoints
     ========================== */

  /* 📺 Large monitors (wider than 1920px) → Slightly larger text */
  @media (min-width: 1921px) {
    html {
      font-size: 18px;
    }
  }

  /* 💻 Laptops (Acer Aspire 7, 1366px–1920px) → Default */
  /* @media (min-width: 1025px) and (max-width: 1920px) {
    html {
      font-size: 11px;
    }
  } */
</style>
